/**
 * Per-user localStorage namespacing.
 *
 * Every per-user key is suffixed with `:<userId>` (or `:anon` when no user
 * is signed in yet — e.g. during signup / pre-onboarding). This guarantees
 * that two users sharing a device cannot read or overwrite each other's
 * cached state, even if sign-out cleanup misses some keys.
 *
 * The current user id is set by useAuth once the session resolves, but it
 * is also read **synchronously at module load** from the Supabase auth
 * token already cached in localStorage (`sb-*-auth-token`). That way the
 * very first synchronous call to getScoped() after a page reload already
 * targets the correct namespace — no flash of wrong language/theme.
 */

const ANON = "anon";
const LEGACY_OWNER_ID_KEY = "ayuni_user_settings_owner_id";

function readSyncFromAuthToken(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("sb-") || !k.endsWith("-auth-token")) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const id =
        parsed?.user?.id ??
        parsed?.currentSession?.user?.id ??
        parsed?.session?.user?.id;
      if (id) return id;
    }
  } catch {
    // ignore malformed token
  }
  return null;
}

let currentUserId: string | null = readSyncFromAuthToken();

/** Per-key flag so we attempt the legacy → namespaced migration only once. */
const migratedKeys = new Set<string>();

export function getCurrentUserId(): string | null {
  return currentUserId;
}

/**
 * Called by useAuth when auth state changes. Pass `null` on sign-out to
 * fall back to the anonymous bucket. Triggers a `userSettingsChanged`
 * event so reactive components re-read with the new namespace.
 */
export function setCurrentUserId(id: string | null): void {
  if (currentUserId === id) return;
  currentUserId = id;
  try {
    window.dispatchEvent(
      new CustomEvent("userScopeChanged", { detail: { userId: id } }),
    );
    window.dispatchEvent(
      new CustomEvent("userSettingsChanged", { detail: { changedKeys: ["*"] } }),
    );
  } catch {
    // ignore (SSR / no window)
  }
}

function suffix(): string {
  return currentUserId || ANON;
}

export function nsKey(base: string): string {
  return `${base}:${suffix()}`;
}

/**
 * One-time migration helper.
 *
 * If the user just signed in and their namespaced bucket is empty but a
 * legacy unnamespaced key exists, claim it only when the legacy
 * `ayuni_user_settings_owner_id` explicitly matches. Ownerless legacy data is
 * unsafe on shared devices, so it is removed instead of inherited.
 */
function maybeMigrate(base: string): void {
  const tag = `${base}:${suffix()}`;
  if (migratedKeys.has(tag)) return;
  migratedKeys.add(tag);

  if (!currentUserId) return;

  try {
    const newKey = nsKey(base);
    if (localStorage.getItem(newKey) !== null) return;
    const legacy = localStorage.getItem(base);
    if (legacy === null) return;

    const ownerId = localStorage.getItem(LEGACY_OWNER_ID_KEY);
    if (ownerId !== currentUserId) {
      // Legacy data is ownerless or belongs to a different user — drop it;
      // never inherit ambiguous patient data into a new account.
      localStorage.removeItem(base);
      return;
    }

    localStorage.setItem(newKey, legacy);
    localStorage.removeItem(base);
  } catch {
    // ignore quota / serialization issues
  }
}

export function getScoped(base: string): string | null {
  try {
    maybeMigrate(base);
    return localStorage.getItem(nsKey(base));
  } catch {
    return null;
  }
}

export function setScoped(base: string, value: string): void {
  try {
    localStorage.setItem(nsKey(base), value);
  } catch {
    // ignore quota
  }
}

export function removeScoped(base: string): void {
  try {
    localStorage.removeItem(nsKey(base));
  } catch {
    // ignore
  }
}
